/**
 * 라우트 정의.
 *
 * 운영 파트너 포털(`partner.metaxtreet.kr`)의 메뉴 구성을 그대로 따른다.
 * 다만 **가입 화면은 없다**(2026-10-20, DB #88) — 관리자는 스스로 만들 수 없고
 * 전체 권한 관리자가 `관리자 계정` 화면에서 만들어 준다.
 * 화면 이름·경로를 맞춰 두면 나중에 운영과 대조하기 쉽다.
 *
 * meta.icon 이 있는 라우트만 사이드바에 뜬다. meta.order 가 표시 순서다 —
 * getRoutes() 는 선언 순서가 아니라 경로 매칭 점수 순으로 주기 때문이다.
 */
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { isSignedIn, user } from '@/utils/auth'

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    /** 있으면 사이드바에 뜬다 */
    icon?: string
    /** 사이드바 표시 순서 */
    order?: number
    /** 로그인 없이 볼 수 있는 화면 */
    public?: boolean
    /** 사이드바에서 불을 켤 메뉴 경로 — 메뉴에 없는 하위 화면(상세 등)이 쓴다 */
    activeMenu?: string
  }
}

export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { title: '로그인', public: true },
  },
  {
    // 비밀번호 바꾸기는 **레이아웃 밖**이다(2026-10-20, DB #88) — 처음 받은 비밀번호로
    // 들어온 사람에게 사이드바와 다른 화면을 먼저 보여 주지 않는다
    path: '/my-password',
    name: 'my-password',
    component: () => import('@/views/MyPasswordView.vue'),
    meta: { title: '비밀번호 바꾸기' },
  },
  {
    path: '/',
    component: () => import('@/layouts/PortalLayout.vue'),
    children: [
      { path: '', redirect: '/dashboard' },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { title: '대시보드', icon: 'Odometer', order: 1 },
      },
      {
        // 지역 관리 — 탭 셋. 앱 목록(앱에 보일 것) · 행정구역(구글이 준 것) · 지정구역(우리가 그린 것). DB #42
        path: 'area',
        name: 'area',
        component: () => import('@/views/area/AreaView.vue'),
        meta: { title: '지역 관리', icon: 'MapLocation', order: 4 },
      },
      {
        // 지정구역 그리기 — 지도를 꽉 써야 해서 탭이 아니라 떼어 둔 화면이다.
        // 사이드바에는 안 뜨고 '지역 관리' 에 불이 켜진다(스팟 상세와 같은 방식).
        path: 'area/zone/new',
        name: 'zone-new',
        component: () => import('@/views/area/ZoneEditorView.vue'),
        meta: { title: '지정구역 그리기', activeMenu: '/area' },
      },
      {
        path: 'area/zone/:zoneId',
        name: 'zone-edit',
        component: () => import('@/views/area/ZoneEditorView.vue'),
        meta: { title: '지정구역 고치기', activeMenu: '/area' },
      },
      {
        path: 'spot-admin',
        name: 'spot-admin',
        component: () => import('@/views/spot/SpotAdminView.vue'),
        meta: { title: '스팟 관리', icon: 'LocationInformation', order: 5 },
      },
      {
        // 스팟 상세 — 목록에서 이름을 누르면 온다. 사이드바에는 안 뜨고 '스팟 관리' 에 불이 켜진다
        path: 'spot-admin/:spotId',
        name: 'spot-detail',
        component: () => import('@/views/spot/SpotDetailView.vue'),
        props: (r) => ({ spotId: Number(r.params.spotId) }),
        meta: { title: '스팟 상세', activeMenu: '/spot-admin' },
      },
      {
        // 분기점 관리 — 로드뷰 동선의 분기점(노드)과 지역. 운영 '지도/분기점 관리' 의 분기점 탭(2026-09-15)
        path: 'branch',
        name: 'branch',
        component: () => import('@/views/branch/BranchView.vue'),
        meta: { title: '분기점 관리', icon: 'Share', order: 6 },
      },
      {
        // 영상 관리 — 유튜브에서 삭제된 영상(DB #128)을 보고 지운다. 스팟은 그대로 · 폐업 · 숨김, 분기점은 지울지 고른다(2026-10-30).
        // 자리는 '분기점 관리' 바로 다음(2026-10-30 사용자)
        path: 'video-manage',
        name: 'video-manage',
        component: () => import('@/views/mapping/VideoManageView.vue'),
        meta: { title: '영상 관리', icon: 'Film', order: 6.5 },
      },
      {
        // 스팟-영상 매핑 — 피드 영상과 스팟을 n:n 으로 잇는다(video_spot_ref). 영상으로 찾기 / 스팟으로 찾기(2026-09-17)
        path: 'video-spot',
        name: 'video-spot',
        component: () => import('@/views/mapping/VideoSpotView.vue'),
        meta: { title: '스팟-영상 매핑', icon: 'VideoCamera', order: 7 },
      },
      // 2026-10-30 잠깐 쓴 서브 메뉴 주소 — 넘겨준다
      { path: 'video/mapping', redirect: (to) => ({ path: '/video-spot', query: to.query }) },
      { path: 'video/manage', redirect: (to) => ({ path: '/video-manage', query: to.query }) },
      { path: 'video', redirect: '/video-spot' },
      {
        // AI 매핑 — 메뉴 하나에 서브 메뉴(2026-10-29 사용자). 부모는 화면이 없고 대시보드로 보낸다
        path: 'ai',
        name: 'ai',
        redirect: '/ai/dashboard',
        meta: { title: 'AI 매핑', icon: 'MagicStick', order: 8.5 },
        children: [
          {
            // 대시보드 — 수집 · 스케줄 · 쿼터 · 검수 · 정확도 · 비용 · 워커 실행 이력
            path: 'dashboard',
            name: 'ai-dashboard',
            component: () => import('@/views/ingest/IngestDashboardView.vue'),
            meta: { title: '대시보드', icon: 'Odometer' },
          },
          {
            // 검수 보드 — AI 가 찾은 영상 속 장소 제안을 판정해 영상-스팟 연결을 만든다(ingest_*, 2026-10-27).
            // 사람이 등록한 영상 · 연결은 검수하지 않는다(2026-10-29)
            path: 'review',
            name: 'ai-review',
            component: () => import('@/views/ingest/IngestBoardView.vue'),
            meta: { title: '검수 보드', icon: 'Finished' },
          },
          {
            // 매핑 현황 — 스팟별로 이어진 영상 · 영상별로 이어진 스팟, 누가(사람 · AI) 이었는지(2026-10-29)
            path: 'mapping',
            name: 'ai-mapping',
            component: () => import('@/views/ingest/IngestMappingView.vue'),
            meta: { title: '매핑 현황', icon: 'Connection' },
          },
          {
            // 수집 계획 — 어디를 · 무엇으로 · 얼마나 자주 모을지(ingest_plan). 실행은 수집 워커가 한다(2026-10-27)
            path: 'plans',
            name: 'ai-plans',
            component: () => import('@/views/ingest/IngestPlanView.vue'),
            meta: { title: '수집 계획', icon: 'Aim' },
          },
        ],
      },
      // 옛 주소 — 2026-10-29 전 즐겨찾기
      { path: 'ingest', redirect: (to) => ({ path: '/ai/review', query: to.query }) },
      { path: 'ingest-plans', redirect: '/ai/plans' },
      {
        // 분기점-영상 매핑 — 로드뷰 영상을 틀며 지도에서 분기점을 찍어 동선을 만든다(video_branch_ref, 2026-09-17)
        path: 'video-branch',
        name: 'video-branch',
        component: () => import('@/views/mapping/VideoBranchView.vue'),
        meta: { title: '분기점-영상 매핑', icon: 'Guide', order: 8 },
      },
      {
        // 코스 관리 — 스팟-영상 연결을 하위 묶음별로 담아 앱의 코스(운영 '그룹')를 만든다(2026-09-22)
        path: 'group',
        name: 'group',
        component: () => import('@/views/group/GroupView.vue'),
        meta: { title: '코스 관리', icon: 'Tickets', order: 9 },
      },
      {
        // 상품 관리 — 스팟·코스에 붙여 파는 것. 옛 구조는 JSON 한 덩이에 담아 찾을 수가 없었다(DB #59·#61·#62)
        path: 'product',
        name: 'product',
        component: () => import('@/views/commerce/ProductView.vue'),
        meta: { title: '상품 관리', icon: 'Goods', order: 10 },
      },
      {
        // 이벤트 관리 — 스팟을 돌며 스탬프를 찍고 경품을 받는 것.
        // 옛 구조는 코스에만 붙었다(spot_group.event_id) — 이제 스팟에도 붙는다(DB #60·#62)
        path: 'event',
        name: 'event',
        component: () => import('@/views/commerce/EventView.vue'),
        meta: { title: '이벤트 관리', icon: 'Present', order: 11 },
      },
      {
        // 실내지도는 '스팟 관리' 의 탭이다(2026-09-15). 옛 주소·이름으로 와도 탭으로 보낸다 — 쿼리(spot_id·floor_id 등)는 그대로
        path: 'indoor',
        name: 'indoor',
        redirect: (to) => ({ name: 'spot-admin', query: { ...to.query, tab: 'indoor' } }),
      },
      {
        // 관리자 계정 — **전체 권한만** 들어온다(2026-10-20, DB #88).
        // 서버도 `/admin/users/**` 를 막는다(WebConfigPaths.ADMIN_ONLY) —
        // 사이드바에서 숨기는 것만으로는 주소를 직접 쳐서 들어올 수 있다
        path: 'admin-users',
        name: 'admin-users',
        component: () => import('@/views/AdminUserView.vue'),
        meta: { title: '관리자 계정', icon: 'User', order: 90 },
      },
    ],
  },
  // 없는 경로는 대시보드로
  { path: '/:pathMatch(.*)*', redirect: '/dashboard' },
]

const router = createRouter({ history: createWebHistory(), routes })

/**
 * 인증 가드.
 *
 * `main.ts` 가 토큰으로 "내가 누구인지" 를 서버에 먼저 물어본 뒤 앱을 띄우므로,
 * 여기서는 바로 판단해도 된다(2026-10-20 — 전에는 Firebase 복원을 기다렸다).
 *
 * **처음 받은 비밀번호면 다른 화면을 보여 주지 않는다.** 전체 권한 관리자가 정해 준
 * 임시 비밀번호를 그대로 쓰면, 그 사람이 비밀번호를 아는 상태가 이어진다.
 */
router.beforeEach((to) => {
  if (!to.meta.public && !isSignedIn()) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  if (to.meta.public && isSignedIn()) {
    return { name: 'dashboard' }
  }
  // **주소에 표식을 남기지 않는다**(2026-10-20). `?first=1` 을 붙였더니 그 주소가
  // `redirect` 로 돌아다니며, 이미 바꾼 사람에게도 강제 화면이 보였다.
  // 강제 여부는 `must_change_pw` 하나로만 정한다
  if (isSignedIn() && user.value?.must_change_pw && to.name !== 'my-password') {
    return { name: 'my-password' }
  }
})

/** 탭 제목 */
router.afterEach((to) => {
  const t = to.meta.title
  document.title = t ? `${t} · AllTheStreet 관리자` : 'AllTheStreet 관리자'
})

export default router
